// Database type definitions for GLYYD

// Profile types - matches existing database schema
export interface UserProfile {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  full_name: string | null;
  date_of_birth?: string | null;
  grad_year: number | null;
  gpa: number | null;
  sat_score: number | null;
  height_feet: number | null;
  height_inches: number | null;
  weight: number | null;
  erg_2k: string | null;
  erg_6k: string | null;
  hometown: string | null;
  high_school: string | null;
  club_team: string | null;
  bio: string | null;
  // Role-specific fields
  user_role?: UserRole;
  phone?: string | null;

  // Coach-specific fields
  institution?: string | null;
  coach_title?: string | null;
  coach_verified?: boolean;
  coaching_certifications?: string[] | null;

  // Parent-specific fields
  child_name?: string | null;
  child_grad_year?: number | null;
  child_high_school?: string | null;
  relationship?: string | null;
  stripe_identity_status?: string | null;
  stripe_identity_verified_at?: string | null;
  stripe_identity_verification_session_id?: string | null;
  stripe_identity_last_error?: string | null;
  created_at: string;
  updated_at: string;
}

// Alternative profile interface for future migration
export interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  hometown: string | null;
  high_school: string | null;
  graduation_year: number | null;
  gpa: number | null;
  sat_score: number | null;
  act_score: number | null;
  height: string | null;
  weight: string | null;
  sport: string;
  position: string | null;
  years_experience: number | null;
  achievements: string | null;
  ncaa_clearinghouse_status: string | null;
  created_at: string;
  updated_at: string;
}

// School types
export interface School {
  id: string;
  name: string;
  location: string | null;
  state: string | null;
  division: Division | null;
  conference: string | null;
  rowing_program_info: RowingProgramInfo | null;
  contact_info: ContactInfo | null;
  requirements: Requirements | null;
  website: string | null;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface RowingProgramInfo {
  men: boolean;
  women: boolean;
  lightweight: boolean;
  openweight: boolean;
  head_coach: string | null;
  assistant_coaches: string[] | null;
  roster_size: number | null;
  boats: string[] | null;
  achievements: string[] | null;
}

export interface ContactInfo {
  coach_email: string | null;
  coach_phone: string | null;
  recruiting_coordinator: string | null;
  mailing_address: string | null;
}

export interface Requirements {
  min_gpa: number | null;
  min_sat: number | null;
  min_act: number | null;
  erg_standards: ErgStandards | null;
  preferred_height_range: HeightRange | null;
  preferred_weight_range: WeightRange | null;
}

export interface ErgStandards {
  men_2k: string | null;
  women_2k: string | null;
  men_6k: string | null;
  women_6k: string | null;
}

export interface HeightRange {
  min: string | null;
  max: string | null;
}

export interface WeightRange {
  min: string | null;
  max: string | null;
}

// Saved schools types
export interface SavedSchool {
  id: string;
  user_id: string;
  school_id: string;
  notes: string | null;
  interest_level: InterestLevel;
  created_at: string;
  updated_at: string;
  // Joined data
  school?: School;
  profile?: UserProfile;
}

// Message types
export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  school_id: string | null;
  subject: string;
  body: string;
  read: boolean;
  archived: boolean;
  created_at: string;
  // Joined data
  sender?: User;
  recipient?: User;
  school?: School;
}

// User types (extends Supabase Auth)
export interface User {
  id: string;
  email: string;
  role: UserRole;
  created_at: string;
  last_sign_in_at: string | null;
  // Joined data
  profile?: UserProfile;
}

// Enum types
export enum Division {
  DivisionI = 'Division I',
  DivisionII = 'Division II',
  DivisionIII = 'Division III',
  NAIA = 'NAIA',
  Club = 'Club',
  Junior = 'Junior College',
}

export enum InterestLevel {
  VeryLow = 1,
  Low = 2,
  Medium = 3,
  High = 4,
  VeryHigh = 5,
}

export enum UserRole {
  Athlete = 'athlete',
  Coach = 'coach',
  Parent = 'parent',
  Admin = 'admin',
  Superadmin = 'superadmin',
}

export interface ProfileRole {
  profile_id: string;
  role: string;
  granted_at: string;
  granted_by: string | null;
}

export enum NCAAStatus {
  NotRegistered = 'not_registered',
  Registered = 'registered',
  Approved = 'approved',
  FinalApproved = 'final_approved',
}

// Form/Input types
export interface ProfileFormData {
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  hometown: string;
  high_school: string;
  graduation_year: string;
  gpa: string;
  sat_score: string;
  act_score: string;
  height: string;
  weight: string;
  position: string;
  years_experience: string;
  achievements: string;
  ncaa_clearinghouse_status: string;
}

// API Response types
export interface ApiResponse<T> {
  data: T | null;
  error: ApiError | null;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// Filter types
export interface SchoolFilters {
  division?: Division[];
  state?: string[];
  conference?: string[];
  hasWomens?: boolean;
  hasMens?: boolean;
  hasLightweight?: boolean;
  searchTerm?: string;
}

export interface ProfileFilters {
  graduation_year?: number[];
  min_gpa?: number;
  sport?: string;
  position?: string[];
  state?: string[];
}

// Pagination types
export interface PaginationParams {
  page: number;
  limit: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Supabase specific types
export type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

// Client-readable Concept2 tables. Database RLS limits rows to the owning
// rower_profiles.profile_id; OAuth credentials and sync state are service-only.
export interface Concept2Workout {
  id: string;
  rower_profile_id: string;
  connection_id: string | null;
  concept2_user_id: number;
  concept2_result_id: number;
  workout_local_at: string;
  workout_at: string | null;
  timezone: string | null;
  machine_type: string;
  workout_type: string;
  distance_meters: number | null;
  time_tenths: number | null;
  stroke_rate: number | null;
  stroke_count: number | null;
  drag_factor: number | null;
  calories_total: number | null;
  wattminutes_total: number | null;
  rest_distance_meters: number | null;
  rest_time_tenths: number | null;
  heart_rate: Json | null;
  source: string | null;
  weight_class: string | null;
  verified: boolean;
  ranked: boolean;
  privacy: string | null;
  comments: string | null;
  time_formatted: string | null;
  verification_code: string | null;
  targets: Json | null;
  vendor_deleted_at: string | null;
  vendor_seen_at: string;
  created_at: string;
  updated_at: string;
}

export interface Concept2WorkoutSegment {
  id: string;
  workout_id: string;
  segment_type: 'split' | 'interval';
  sequence: number;
  interval_type: string | null;
  machine_type: string | null;
  distance_meters: number | null;
  time_tenths: number | null;
  stroke_rate: number | null;
  calories_total: number | null;
  wattminutes_total: number | null;
  rest_time_tenths: number | null;
  rest_distance_meters: number | null;
  heart_rate: Json | null;
  targets: Json | null;
}

export interface Concept2WorkoutStroke {
  workout_id: string;
  stroke_index: number;
  incremental_time_tenths: number | null;
  workout_time_tenths: number;
  incremental_distance_decimeters: number | null;
  pace_tenths: number | null;
  stroke_rate: number | null;
  heart_rate: number | null;
}

export interface Concept2WorkoutMetadata {
  workout_id: string;
  client_version: string | null;
  pm_version: number | null;
  firmware_version: string | null;
  serial_number: string | null;
  device: string | null;
  device_os: string | null;
  device_os_version: string | null;
  erg_model_type: number | null;
  hr_type: string | null;
  other: string | null;
  vendor_seen_at: string;
  created_at: string;
  updated_at: string;
}

export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: UserProfile;
        Insert: Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>;
      };
      schools: {
        Row: School;
        Insert: Omit<School, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<School, 'id' | 'created_at' | 'updated_at'>>;
      };
      saved_schools: {
        Row: SavedSchool;
        Insert: Omit<SavedSchool, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<SavedSchool, 'id' | 'created_at' | 'updated_at'>>;
      };
      messages: {
        Row: Message;
        Insert: Omit<Message, 'id' | 'created_at'>;
        Update: Partial<Omit<Message, 'id' | 'created_at'>>;
      };
      waitlist: {
        Row: {
          id: string;
          email: string;
          created_at: string;
          source: string;
          referrer: string | null;
          metadata: Record<string, any>;
        };
        Insert: {
          id?: string;
          email: string;
          created_at?: string;
          source?: string;
          referrer?: string | null;
          metadata?: Record<string, any>;
        };
        Update: {
          id?: string;
          email?: string;
          created_at?: string;
          source?: string;
          referrer?: string | null;
          metadata?: Record<string, any>;
        };
      };
      concept2_workouts: {
        Row: Concept2Workout;
        Insert: Partial<Concept2Workout> &
          Pick<
            Concept2Workout,
            | 'rower_profile_id'
            | 'concept2_user_id'
            | 'concept2_result_id'
            | 'workout_local_at'
            | 'machine_type'
          >;
        Update: Partial<Concept2Workout>;
      };
      concept2_workout_segments: {
        Row: Concept2WorkoutSegment;
        Insert: Partial<Concept2WorkoutSegment> &
          Pick<Concept2WorkoutSegment, 'workout_id' | 'segment_type' | 'sequence'>;
        Update: Partial<Concept2WorkoutSegment>;
      };
      concept2_workout_strokes: {
        Row: Concept2WorkoutStroke;
        Insert: Partial<Concept2WorkoutStroke> &
          Pick<Concept2WorkoutStroke, 'workout_id' | 'stroke_index' | 'workout_time_tenths'>;
        Update: Partial<Concept2WorkoutStroke>;
      };
      concept2_workout_metadata: {
        Row: Concept2WorkoutMetadata;
        Insert: Partial<Concept2WorkoutMetadata> & Pick<Concept2WorkoutMetadata, 'workout_id'>;
        Update: Partial<Concept2WorkoutMetadata>;
      };
      profile_roles: {
        Row: ProfileRole;
        Insert: Partial<ProfileRole> & Pick<ProfileRole, 'profile_id' | 'role'>;
        Update: Partial<ProfileRole>;
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      [_ in never]: never;
    };
    Enums: {
      [_ in never]: never;
    };
  };
}

// Helper types
export type Tables<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Row'];
export type Insertable<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Insert'];
export type Updatable<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Update'];
